/**
 * Kiosk — fullscreen `/kiosk` route. Cycles the display scenes on a
 * configurable interval. No sidebar, no mobile nav. Always shows live data.
 * Sets `data-kiosk` on the root so tokens.css can suppress caustics and
 * apply kiosk-specific contrast adjustments.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { AccessKeyGate } from '../../components/AccessKeyGate';
import { userDataApi } from '../../lib/userdata-api';
import { DEFAULT_SCENE_IDS, SCENE_REGISTRY, type SceneId } from './sceneRegistry';
import { useIdleCursor } from './useIdleCursor';

// ── Kiosk settings shape ──────────────────────────────────────────────────────

interface KioskSettings {
  sceneIntervalSec: number;
  autoLaunch: boolean;
  scenes: SceneId[];
}

const DEFAULTS: KioskSettings = {
  sceneIntervalSec: 20,
  autoLaunch: false,
  scenes: [...DEFAULT_SCENE_IDS],
};

function narrowKioskSettings(raw: unknown): KioskSettings {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return DEFAULTS;
  }
  const obj = raw as Record<string, unknown>;

  const sceneIntervalSec =
    typeof obj.sceneIntervalSec === 'number' && obj.sceneIntervalSec > 0
      ? obj.sceneIntervalSec
      : DEFAULTS.sceneIntervalSec;

  const autoLaunch =
    typeof obj.autoLaunch === 'boolean' ? obj.autoLaunch : DEFAULTS.autoLaunch;

  // Legacy persisted settings may still list removed scenes (e.g. the
  // old 'tank-vision' scene) — they are skipped, never deleted upstream.
  const scenes: SceneId[] = Array.isArray(obj.scenes)
    ? (obj.scenes as string[]).filter(
        (s): s is SceneId => s === 'big-numbers' || s === 'schedule',
      )
    : [...DEFAULTS.scenes];

  return {
    sceneIntervalSec,
    autoLaunch,
    scenes: scenes.length > 0 ? scenes : [...DEFAULTS.scenes],
  };
}

// ── Reduced-motion helper ─────────────────────────────────────────────────────

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Kiosk() {
  const [settings, setSettings] = useState<KioskSettings>(DEFAULTS);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const idleCursor = useIdleCursor(3000);
  const reduced = useReducedMotion();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load persisted kiosk settings.
  useEffect(() => {
    void (async () => {
      const result = await userDataApi.get('kiosk');
      if (result.ok) {
        setSettings(narrowKioskSettings(result.value.data));
      }
    })();
  }, []);

  // Advance to the next scene.
  const advance = useCallback(() => {
    if (reduced) {
      setSceneIdx((i) => (i + 1) % settings.scenes.length);
      return;
    }
    setFading(true);
    setTimeout(() => {
      setSceneIdx((i) => (i + 1) % settings.scenes.length);
      setFading(false);
    }, 400);
  }, [reduced, settings.scenes.length]);

  // Start / restart the rotation interval whenever the interval setting changes.
  useEffect(() => {
    if (intervalRef.current !== null) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(advance, settings.sceneIntervalSec * 1000);
    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
  }, [advance, settings.sceneIntervalSec]);

  const activeSceneIds = settings.scenes.filter((id) =>
    SCENE_REGISTRY.some((e) => e.id === id),
  );

  const currentId = activeSceneIds[sceneIdx % activeSceneIds.length];
  const entry = SCENE_REGISTRY.find((e) => e.id === currentId);
  const SceneComponent = entry?.component ?? null;

  return (
    <div
      data-kiosk
      className="fixed inset-0 bg-black text-white overflow-hidden"
      style={{ cursor: idleCursor ? 'none' : 'default' }}
      data-testid="kiosk-root"
    >
      {/* Scene container with cross-fade */}
      <div
        className="absolute inset-0"
        style={{
          opacity: fading ? 0 : 1,
          transition: reduced ? 'none' : 'opacity 0.4s ease',
        }}
        data-testid="kiosk-scene-container"
      >
        {SceneComponent !== null ? <SceneComponent /> : null}
      </div>

      {/* Passcode prompt — the kiosk device needs the key once too */}
      <AccessKeyGate />

      {/* Scene title overlay — top-right, fades out with cursor */}
      {!idleCursor && entry !== undefined ? (
        <div className="absolute top-6 right-8 text-white/30 text-sm tracking-widest uppercase select-none pointer-events-none">
          {entry.title}
        </div>
      ) : null}

      {/* Dot indicators — bottom-center */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 pointer-events-none"
        role="tablist"
        aria-label="Kiosk scene indicator"
      >
        {activeSceneIds.map((id, i) => (
          <span
            key={id}
            role="tab"
            aria-selected={i === sceneIdx % activeSceneIds.length}
            className={[
              'w-1.5 h-1.5 rounded-full transition-all duration-300',
              i === sceneIdx % activeSceneIds.length
                ? 'bg-white scale-125'
                : 'bg-white/30',
            ].join(' ')}
          />
        ))}
      </div>
    </div>
  );
}
